import apiClient from "@services/api/apiClient.jsx";

/**
 * 프린트 관련 API 서비스
 */
const printApi = {
    /**
     * Firebase 미디어 리스트 조회
     * @param {Object} params - 검색 조건
     * @param {string} params.startDatetime - 시작일시 (YYYYMMDDHHMM)
     * @param {string} params.endDatetime - 종료일시 (YYYYMMDDHHMM)
     * @param {string} params.branchNo - 브랜치 번호
     * @param {number} params.page - 페이지 번호 (0부터 시작)
     * @param {number} params.size - 페이지 크기
     * @param {string} params.sortDirection - 정렬 방향 (asc/desc)
     * @returns {Promise} 미디어 리스트 응답
     */
    getMediaList: async (params = {}) => {
        try {
            const response = await apiClient.get('/print/mediaList', {
                params: {
                    startDatetime: params.startDatetime,
                    endDatetime: params.endDatetime,
                    branchNo: params.branchNo,
                    page: params.page || 0,
                    size: params.size || 10,
                    sortDirection: params.sortDirection || 'desc'
                }
            });
            return response.data;
        } catch (error) {
            console.error('미디어 리스트 조회 실패:', error);
            throw error;
        }
    },

    /**
     * 사진 프린트 요청을 대기열에 추가
     * @param {Object} request - 사진 프린트 요청 데이터
     * @param {string} request.uuid - 미디어 UUID
     * @param {string} request.year - 년도
     * @param {string} request.month - 월
     * @param {string} request.day - 일
     * @param {string} request.path - 파일 경로
     * @param {boolean} request.isCut - 컷팅 여부
     * @param {number} request.amount - 수량
     * @returns {Promise} 프린트 요청 응답
     */
    queuePhotoPrint: async (request) => {
        try {
            const response = await apiClient.post('/print/photo/queue', request);
            return response.data;
        } catch (error) {
            console.error('사진 프린트 요청 실패:', error);
            throw error;
        }
    },

    /**
     * QR 프린트 요청을 대기열에 추가
     * @param {Object} request - QR 프린트 요청 데이터
     * @param {string} request.uuid - 미디어 UUID
     * @param {string} request.year - 년도
     * @param {string} request.month - 월
     * @param {string} request.day - 일
     * @returns {Promise} 프린트 요청 응답
     */
    queueQrPrint: async (request) => {
        try {
            const response = await apiClient.post('/print/qr/queue', request);
            return response.data;
        } catch (error) {
            console.error('QR 프린트 요청 실패:', error);
            throw error;
        }
    },

    /**
     * 프린트 요청 직접 생성 (관리자용)
     * @param {Object} request - 프린트 요청 데이터
     * @param {string} request.requestType - 요청 타입 (PHOTO/QR)
     * @param {string} request.branchNo - 브랜치 번호
     * @param {string} request.robotNo - 로봇 번호
     * @param {string} request.uuid - 미디어 UUID
     * @param {string} request.year - 년도
     * @param {string} request.month - 월
     * @param {string} request.day - 일
     * @param {string} request.filePath - 파일 경로 (사진용)
     * @param {boolean} request.isCut - 컷팅 여부 (사진용)
     * @param {number} request.amount - 수량 (사진용)
     * @returns {Promise} 프린트 요청 응답
     */
    createPrintRequest: async (request) => {
        try {
            const response = await apiClient.post('/print/requests', request);
            return response.data;
        } catch (error) {
            console.error('프린트 요청 생성 실패:', error);
            throw error;
        }
    },

    /**
     * 프린트 요청 목록 조회
     * @param {Object} params - 검색 조건
     * @returns {Promise} 프린트 요청 목록 응답
     */
    getPrintRequests: async (params = {}) => {
        try {
            const response = await apiClient.get('/print/requests', {
                params: {
                    branchNo: params.branchNo,
                    robotNo: params.robotNo,
                    requestStatus: params.requestStatus,
                    requestType: params.requestType,
                    startDate: params.startDate,
                    endDate: params.endDate,
                    page: params.page || 0,
                    size: params.size || 20,
                    sortDirection: params.sortDirection || 'desc'
                }
            });
            return response.data;
        } catch (error) {
            console.error('프린트 요청 목록 조회 실패:', error);
            throw error;
        }
    },

    /**
     * 프린트 요청 취소
     * @param {number} id - 프린트 요청 ID
     * @returns {Promise} 프린트 요청 응답
     */
    cancelPrintRequest: async (id) => {
        try {
            const response = await apiClient.patch(`/print/requests/${id}/cancel`);
            return response.data;
        } catch (error) {
            console.error('프린트 요청 취소 실패:', error);
            throw error;
        }
    },

    /**
     * 프린트 요청 재시도
     * @param {number} id - 프린트 요청 ID
     * @returns {Promise} 프린트 요청 응답
     */
    retryPrintRequest: async (id) => {
        try {
            const response = await apiClient.patch(`/print/requests/${id}/retry`);
            return response.data;
        } catch (error) {
            console.error('프린트 요청 재시도 실패:', error);
            throw error;
        }
    }
};

export default printApi;
